import { useTranslation } from 'react-i18next';
import {
  Paper,
  Typography,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Tooltip,
  Button,
} from '@gapartners/ui';
import { Phone as PhoneIcon } from '@mui/icons-material';
import { getStatusChipColor, getStatusLabel } from '@/utils/agentStatus';

export interface AgentQueueInfo {
  queueName: string;
  waitingContacts: number;
}

export interface AgentStatusDisplayData {
  email: string;
  connectId: string;
  status: string;
  statusStartTime: string | null;
  routingProfileName: string;
  activeCalls: number;
  queueInfo: AgentQueueInfo | null;
}

interface AgentStatusTableProps {
  members: AgentStatusDisplayData[];
  isLoading: boolean;
  totalActiveCalls: number;
  onRefresh: () => void;
  isRefreshing: boolean;
}

/**
 * Reusable table component for displaying agent status information
 * Used by both managers (team view) and agents (queue members view)
 */
export function AgentStatusTable({
  members,
  isLoading,
  totalActiveCalls,
  onRefresh,
  isRefreshing,
}: AgentStatusTableProps) {
  const { t } = useTranslation();

  const getCallsChipColor = (
    calls: number
  ): 'default' | 'primary' | 'warning' | 'error' => {
    if (calls === 0) return 'default';
    if (calls === 1) return 'primary';
    if (calls <= 2) return 'warning';
    return 'error';
  };

  const getQueueChipColor = (
    waiting: number
  ): 'default' | 'warning' | 'error' => {
    if (waiting === 0) return 'default';
    if (waiting <= 2) return 'warning';
    return 'error';
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('components.manager.liveAgentStatus.title')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {!isLoading && members.length > 0 && (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('components.manager.totalActiveCalls')}:
              </Typography>
              <Chip
                label={totalActiveCalls}
                color={totalActiveCalls > 0 ? 'primary' : 'default'}
                size="small"
                icon={
                  totalActiveCalls > 0 ? (
                    <PhoneIcon sx={{ fontSize: '1rem !important' }} />
                  ) : undefined
                }
              />
            </>
          )}
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={onRefresh}
            disabled={isRefreshing}
          >
            {isRefreshing ? t('common.loading') : t('common.refresh')}
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={40} />
        </Box>
      ) : members.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('components.manager.liveAgentStatus.noTeamMembers')}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('components.manager.liveAgentStatus.agentName')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('components.manager.liveAgentStatus.email')}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  {t('components.manager.liveAgentStatus.routingProfile')}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  {t('components.manager.liveAgentStatus.status')}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  {t('components.manager.liveAgentStatus.activeCalls')}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  {t('components.manager.liveAgentStatus.queueStatus')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map(member => (
                <TableRow key={member.email}>
                  <TableCell sx={{ fontWeight: 500 }}>{member.email}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {member.email}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={member.routingProfileName}>
                      <Typography variant="caption">
                        {member.routingProfileName.length > 15
                          ? `${member.routingProfileName.substring(0, 15)}…`
                          : member.routingProfileName}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={getStatusLabel(member.status, t)}
                      color={getStatusChipColor(member.status)}
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: 80 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={member.activeCalls}
                      color={getCallsChipColor(member.activeCalls)}
                      size="small"
                      icon={
                        member.activeCalls > 0 ? (
                          <PhoneIcon sx={{ fontSize: '1rem' }} />
                        ) : undefined
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    {member.queueInfo ? (
                      <Tooltip title={`Queue: ${member.queueInfo.queueName}`}>
                        <Chip
                          label={`${member.queueInfo.waitingContacts} waiting`}
                          color={getQueueChipColor(
                            member.queueInfo.waitingContacts
                          )}
                          size="small"
                          variant="outlined"
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
